import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { Subscription } from '../types';
import { Trash2, Search, Filter, User, Clock, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export function SubscriptionsPage() {
  const queryClient = useQueryClient();
  const [selectedApp, setSelectedApp] = useState<string>('');
  const [search, setSearch] = useState('');

  const { data: apps } = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.getApplications(1, 100),
  });

  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ['subscriptions', selectedApp],
    queryFn: () => api.getSubscriptions(selectedApp || undefined, 1, 100),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteSubscription(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });

  const handleDelete = (sub: Subscription) => {
    if (confirm('Are you sure you want to delete this subscription?')) {
      deleteMutation.mutate(sub.id);
    }
  };

  const filteredSubscriptions = subscriptions?.data?.filter(sub => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      sub.endpoint.toLowerCase().includes(searchLower) ||
      sub.userId?.toLowerCase().includes(searchLower) ||
      sub.topics?.some(t => t.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by endpoint, user ID, or topic..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
          <div className="w-full md:w-64">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <select
                value={selectedApp}
                onChange={(e) => setSelectedApp(e.target.value)}
                className="input pl-10 appearance-none"
              >
                <option value="">All Applications</option>
                {apps?.data?.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Subscriptions List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">User</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Endpoint</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Topics</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Last Active</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {filteredSubscriptions?.map((sub) => (
                  <tr key={sub.id} className="hover:bg-slate-700/30">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-white">
                          {sub.userId || 'Anonymous'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs text-slate-400 max-w-[200px] truncate block">
                        {sub.endpoint.slice(0, 50)}...
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {sub.topics?.length > 0 ? (
                          sub.topics.slice(0, 3).map((topic, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                              {topic}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-500">None</span>
                        )}
                        {sub.topics?.length > 3 && (
                          <span className="text-xs text-slate-500">+{sub.topics.length - 3} more</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {sub.isActive ? (
                          <span className="flex items-center gap-1 text-green-400 text-sm">
                            <span className="h-2 w-2 rounded-full bg-green-400"></span>
                            Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-400 text-sm">
                            <AlertCircle className="h-4 w-4" />
                            Inactive ({sub.failureCount} failures)
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Clock className="h-4 w-4" />
                        {formatDistanceToNow(new Date(sub.lastActiveAt), { addSuffix: true })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(sub)}
                        className="btn btn-danger p-2"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredSubscriptions?.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                      No subscriptions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stats */}
      {subscriptions?.pagination && (
        <div className="text-sm text-slate-400 text-center">
          Showing {filteredSubscriptions?.length} of {subscriptions.pagination.total} subscriptions
        </div>
      )}
    </div>
  );
}
